import React, { useMemo, useState } from 'react';
import PageHeader from '../../components/shared/PageHeader';
import DataToolbar, { StatusPill, Card } from '../../components/shared/DataToolbar';
import { LoadingState, ErrorState, EmptyResults } from '../../components/shared/Pending';
import { useApi } from '../../api/useApi';
import { listAdminPropertyReviews, updatePropertyReview } from '../../api/adminApi';
import { Star, Check, X, Flag } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';

export default function AdminReviews() {
  const { data, loading, error, refetch } = useApi(listAdminPropertyReviews, []);
  const rows = useMemo(() => Array.isArray(data) ? data : (data?.items || data || []), [data]);
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('');
  const filtered = rows.filter(r => (!q || `${r.title || ''} ${r.body || ''}`.toLowerCase().includes(q.toLowerCase())) && (!status || r.status === status.toLowerCase()));

  const patch = async (r, payload) => { try { await updatePropertyReview(r.id, payload); toast.success('Updated'); refetch(); } catch (e) { toast.error(e?.message); } };

  return (
    <div>
      <PageHeader title="Property reviews" description="Approve, reject or unpublish reviews." />
      <DataToolbar search={q} onSearch={setQ} placeholder="Search reviews…"
        filters={[{ label: 'Status', value: status, options: ['All','Published','Pending','Unpublished','Rejected'], onChange: setStatus }]} />
      {loading ? <LoadingState /> : error ? <ErrorState error={error} onRetry={refetch} />
        : filtered.length === 0 ? <EmptyResults icon={Star} title="No reviews" description="No reviews match your filters." />
        : (
          <div className="space-y-3">
            {filtered.map(r => (
              <Card key={r.id} className="p-5">
                <div className="flex items-start gap-3">
                  {r.customer?.avatar && <img src={r.customer.avatar} className="h-10 w-10 rounded-full object-cover" alt="" />}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="font-semibold text-slate-900">{r.customer?.name}</div>
                      <div className="flex">{[1,2,3,4,5].map(i => <Star key={i} className={`h-3.5 w-3.5 ${i <= (r.rating||0) ? 'text-amber-400 fill-amber-400' : 'text-slate-200 fill-slate-200'}`} />)}</div>
                      <StatusPill status={r.status} />
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5">{r.property?.title} · {r.created_at}</div>
                    {r.title && <div className="font-medium text-slate-900 mt-2">{r.title}</div>}
                    <div className="text-sm text-slate-600 mt-1">{r.body}</div>
                  </div>
                  <div className="flex flex-col gap-2">
                    {r.status !== 'published' && <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 h-8" onClick={() => patch(r, { status: 'published' })}><Check className="h-4 w-4 mr-1" /> Publish</Button>}
                    {r.status !== 'unpublished' && <Button size="sm" variant="outline" className="h-8" onClick={() => patch(r, { status: 'unpublished' })}><X className="h-4 w-4 mr-1" /> Unpublish</Button>}
                    {r.status !== 'rejected' && <Button size="sm" variant="outline" className="h-8 text-rose-600 border-rose-200" onClick={() => patch(r, { status: 'rejected' })}><Flag className="h-4 w-4 mr-1" /> Reject</Button>}
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
    </div>
  );
}
